import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input, Select } from '../components/ui/input.js';

type SmsKind = 'wildix' | 'commio' | 'twilio';
type SmsStatus = 'active' | 'disabled';

interface SmsProvider {
  id: string;
  kind: SmsKind;
  display_name: string;
  from_number: string;
  is_default: boolean;
  status: SmsStatus;
  last_send_success_at: string | null;
  last_send_error: string | null;
  created_at: string;
}

interface ListResponse {
  data: SmsProvider[];
}

const KIND_LABEL: Record<SmsKind, string> = {
  wildix: 'Wildix PBX',
  commio: 'Commio',
  twilio: 'Twilio',
};

const STATUS_TONE: Record<SmsStatus, 'success' | 'neutral'> = {
  active: 'success',
  disabled: 'neutral',
};

interface FormState {
  id: string | null;
  kind: SmsKind;
  display_name: string;
  from_number: string;
  is_default: boolean;
  status: SmsStatus;
}

const EMPTY_FORM: FormState = {
  id: null,
  kind: 'wildix',
  display_name: '',
  from_number: '',
  is_default: false,
  status: 'active',
};

export function SmsProvidersPage(): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['sms-providers'],
    queryFn: () => api.get('/v1/admin/sms-providers') as Promise<ListResponse>,
  });

  const save = useMutation({
    mutationFn: async (state: FormState) => {
      if (state.id) {
        return api.patch(`/v1/admin/sms-providers/${state.id}`, {
          display_name: state.display_name,
          from_number: state.from_number,
          status: state.status,
        });
      }
      const created = (await api.post('/v1/admin/sms-providers', {
        kind: state.kind,
        display_name: state.display_name,
        from_number: state.from_number,
        is_default: state.is_default,
        status: state.status,
      })) as SmsProvider;
      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-providers'] });
      setForm(null);
    },
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => api.post(`/v1/admin/sms-providers/${id}/set-default`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-providers'] }),
  });

  const removeOne = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/sms-providers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-providers'] }),
  });

  const columns: Column<SmsProvider>[] = [
    {
      key: 'name',
      header: 'Display name',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.display_name}</span>
          <span className="text-xs text-muted-foreground">{KIND_LABEL[row.kind]}</span>
        </div>
      ),
    },
    { key: 'from_number', header: 'From number', render: (row) => row.from_number },
    {
      key: 'default',
      header: 'Default',
      render: (row) =>
        row.is_default ? (
          <Badge tone="success">default</Badge>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDefault.mutate(row.id)}
            disabled={setDefault.isPending}
          >
            Make default
          </Button>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setForm({
                id: row.id,
                kind: row.kind,
                display_name: row.display_name,
                from_number: row.from_number,
                is_default: row.is_default,
                status: row.status,
              })
            }
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`Delete SMS provider "${row.display_name}"?`)) removeOne.mutate(row.id);
            }}
            disabled={removeOne.isPending}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">SMS Providers</h1>
          <p className="text-sm text-muted-foreground">
            Configure outbound SMS dispatch. The default provider is used for
            event reminders and STOP/HELP confirmations.
          </p>
        </div>
        <Button onClick={() => setForm({ ...EMPTY_FORM })}>New provider</Button>
      </div>

      {form && (
        <Card>
          <CardHeader>
            <CardTitle>{form.id ? 'Edit SMS provider' : 'New SMS provider'}</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(form);
              }}
            >
              <label className="text-sm space-y-1">
                <span>Kind</span>
                <Select
                  value={form.kind}
                  disabled={!!form.id}
                  onChange={(e) =>
                    setForm({ ...form, kind: e.target.value as SmsKind })
                  }
                >
                  <option value="wildix">Wildix PBX</option>
                  <option value="commio" disabled>
                    Commio (coming soon)
                  </option>
                  <option value="twilio" disabled>
                    Twilio (coming soon)
                  </option>
                </Select>
              </label>
              <label className="text-sm space-y-1">
                <span>Display name</span>
                <Input
                  required
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                />
              </label>
              <label className="text-sm space-y-1">
                <span>From number (E.164 or 10-digit)</span>
                <Input
                  required
                  value={form.from_number}
                  onChange={(e) => setForm({ ...form, from_number: e.target.value })}
                />
              </label>
              <label className="text-sm space-y-1">
                <span>Status</span>
                <Select
                  value={form.status}
                  onChange={(e) =>
                    setForm({ ...form, status: e.target.value as SmsStatus })
                  }
                >
                  <option value="active">active</option>
                  <option value="disabled">disabled</option>
                </Select>
              </label>
              {!form.id && (
                <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                  />
                  <span>Set as default provider</span>
                </label>
              )}
              {save.error instanceof ApiError && (
                <div className="md:col-span-2 text-sm text-danger">
                  {save.error.message}
                </div>
              )}
              <div className="md:col-span-2 flex items-center gap-2 justify-end">
                <Button variant="ghost" type="button" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : form.id ? 'Save' : 'Create'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : error ? (
            <div className="p-8 text-center text-sm text-danger">
              {(error as Error).message}
            </div>
          ) : !data?.data.length ? (
            <EmptyState
              title="No SMS providers yet"
              description="Add a Wildix or other SMS provider to start sending text reminders."
              action={<Button onClick={() => setForm({ ...EMPTY_FORM })}>New provider</Button>}
            />
          ) : (
            <DataTable<SmsProvider>
              columns={columns}
              data={data.data}
              rowKey={(r) => r.id}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
